import { useDndMonitor } from '@dnd-kit/core'
import confetti from 'canvas-confetti'
import { addDays, format, isToday, parseISO, startOfWeek } from 'date-fns'
import {
  CalendarDaysIcon,
  CheckCheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  LockIcon,
  TriangleAlertIcon,
} from 'lucide-react'
import { type CSSProperties, useEffect, useMemo, useState } from 'react'
import {
  CardBoard,
  DraggableCard,
  DroppableSlot,
} from '@/components/shared/card-board'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { toKey } from '../data/homework-data'
import type { HomeworkTask } from '../data/types'
import { useHomework } from '../providers/homework-provider'
import { TaskCard } from './task-card'

interface WeekStripProps {
  weekStart: Date
  onNavigate: (direction: -1 | 1) => void
  onToday: () => void
  isCurrentWeek: boolean
}

/**
 * Unconditionally resolves the drag-source highlight when a drag ends —
 * on a successful drop (also handled by `CardBoard`'s `onPlace`), a drop
 * outside every slot, or an Esc-cancel. Must render as a child of
 * `CardBoard` so `useDndMonitor` can see its `DndContext`.
 */
export function DragEndReset({ onEnd }: { onEnd: () => void }) {
  useDndMonitor({ onDragEnd: onEnd, onDragCancel: onEnd })
  return null
}

function DraggableTaskCard({
  task,
  locked,
  isDragSource,
  justDropped,
  enterIndex,
}: {
  task: HomeworkTask
  locked: boolean
  isDragSource: boolean
  /** Card was just dropped here — play the "thump" settle */
  justDropped?: boolean
  /** Position in the board's entrance stagger */
  enterIndex?: number
}) {
  return (
    <DraggableCard
      id={task.id}
      data={{ task }}
      disabled={locked}
      isDragSource={isDragSource}
    >
      <div
        className={cn(
          justDropped && 'animate-card-settle',
          enterIndex !== undefined && 'animate-card-enter',
        )}
        style={
          enterIndex !== undefined
            ? ({ '--enter-delay': `${enterIndex * 90}ms` } as CSSProperties)
            : undefined
        }
      >
        <TaskCard task={task} />
      </div>
    </DraggableCard>
  )
}

/** A single 7-day row of the agenda for one week. */
function WeekRow({
  weekStart,
  locked,
  overdue,
  dragTaskId,
  droppedTaskId,
}: {
  weekStart: Date
  locked: boolean
  /** Backlog row for a past week with missed work */
  overdue?: boolean
  dragTaskId?: string
  /** Task that just landed via drag-and-drop */
  droppedTaskId?: string | null
}) {
  const { tasks, isCompleted, showCompleted } = useHomework()

  const weekStartKey = toKey(weekStart)
  const weekEndKey = toKey(addDays(weekStart, 6))
  const weekTasks = tasks.filter(
    (t) => t.date >= weekStartKey && t.date <= weekEndKey,
  )

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

  return (
    <div className="grid min-w-[980px] grid-cols-7 gap-3">
      {days.map((day, dayIndex) => {
        const dayKey = toKey(day)
        const allDayTasks = weekTasks.filter((t) => t.date === dayKey)
        // Backlog rows only ever surface missed (incomplete) work
        const dayTasks =
          showCompleted && !overdue
            ? allDayTasks
            : allDayTasks.filter((t) => !isCompleted(t))
        const allDone = allDayTasks.length > 0 && dayTasks.length === 0
        const today = !overdue && isToday(day)

        return (
          <DroppableSlot
            key={dayKey}
            id={dayKey}
            disabled={locked}
            className="flex min-w-0 flex-col gap-2"
          >
            <div
              className={cn(
                'flex items-center gap-2 rounded-md px-2 py-1.5',
                today
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground',
                overdue && 'py-1',
              )}
            >
              <span className="text-xs font-medium tracking-wide uppercase">
                {format(day, 'EEE')}
              </span>
              <span
                className={cn(
                  'text-sm font-semibold tabular-nums',
                  !today && 'text-foreground',
                  overdue && 'text-muted-foreground',
                )}
              >
                {format(day, overdue ? 'MMM d' : 'd')}
              </span>
              {today && (
                <span className="ml-auto text-[10px] font-medium tracking-wide uppercase">
                  Today
                </span>
              )}
            </div>

            <div className={cn('flex flex-col gap-2', !overdue && 'min-h-16')}>
              {dayTasks.length === 0 ? (
                overdue ? null : (
                  <div className="flex h-16 items-center justify-center rounded-lg border border-dashed text-xs text-muted-foreground/60">
                    {locked ? (
                      <LockIcon className="size-3.5" aria-hidden="true" />
                    ) : allDone ? (
                      'All done'
                    ) : (
                      'Rest day'
                    )}
                  </div>
                )
              ) : (
                dayTasks.map((task, taskIndex) => {
                  const justDropped = droppedTaskId === task.id
                  return (
                    <DraggableTaskCard
                      key={task.id}
                      task={task}
                      locked={locked}
                      isDragSource={dragTaskId === task.id}
                      justDropped={justDropped}
                      enterIndex={
                        justDropped ? undefined : dayIndex + taskIndex
                      }
                    />
                  )
                })
              )}
            </div>
          </DroppableSlot>
        )
      })}
    </div>
  )
}

export function WeekStrip({
  weekStart,
  onNavigate,
  onToday,
  isCurrentWeek,
}: WeekStripProps) {
  const {
    tasks,
    sprints,
    isWeekLocked,
    isCompleted,
    toggleCompleted,
    moveTask,
  } = useHomework()

  const weekStartKey = toKey(weekStart)
  const sprint = sprints.find((s) => s.startDate === weekStartKey)
  const locked = isWeekLocked(weekStart)

  const [dragTask, setDragTask] = useState<HomeworkTask | null>(null)
  const [droppedTaskId, setDroppedTaskId] = useState<string | null>(null)

  // Clear the settle animation flag once it has played
  useEffect(() => {
    if (!droppedTaskId) return
    const timer = setTimeout(() => setDroppedTaskId(null), 450)
    return () => clearTimeout(timer)
  }, [droppedTaskId])

  // Past weeks (before the viewed current week) that still have missed
  // work. Each disappears as soon as its incomplete tasks are completed
  // or dragged into the current week — never more rows than necessary.
  const backlogWeeks = useMemo(() => {
    if (!isCurrentWeek) return []
    const weekKeys = new Set<string>()
    for (const task of tasks) {
      if (task.date >= weekStartKey || isCompleted(task)) continue
      weekKeys.add(toKey(startOfWeek(parseISO(task.date), { weekStartsOn: 1 })))
    }
    return [...weekKeys].sort().map((key) => parseISO(key))
  }, [isCurrentWeek, tasks, weekStartKey, isCompleted])

  const weekTasks = tasks.filter(
    (t) => t.date >= weekStartKey && t.date <= toKey(addDays(weekStart, 6)),
  )
  const doneCount = weekTasks.filter((t) => isCompleted(t)).length
  // Only manually-completed assignments can be bulk-completed; submission-
  // review work finishes through its review lifecycle.
  const manualRemaining = weekTasks.filter(
    (t) => !isCompleted(t) && !t.platformTracked,
  )

  const missedCount = useMemo(() => {
    if (!isCurrentWeek) return 0
    return tasks.filter((t) => t.date < weekStartKey && !isCompleted(t)).length
  }, [isCurrentWeek, tasks, weekStartKey, isCompleted])

  return (
    <section
      aria-label="Weekly homework agenda"
      className="flex flex-col gap-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold tracking-tight">
            {sprint ? `Sprint ${sprint.number}` : 'Off week'}
          </h2>
          {sprint && sprint.theme && (
            <Badge variant="secondary" className="hidden sm:inline-flex">
              {sprint.theme}
            </Badge>
          )}
          {locked && (
            <Badge variant="outline">
              <LockIcon aria-hidden="true" />
              Unlocks {format(weekStart, 'EEE, MMM d')}
            </Badge>
          )}
          {!locked && weekTasks.length > 0 && (
            <span className="text-sm text-muted-foreground">
              {doneCount}/{weekTasks.length} done
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {format(weekStart, 'MMM d')} –{' '}
            {format(addDays(weekStart, 6), 'MMM d, yyyy')}
          </span>
          {!locked && manualRemaining.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={(event) => {
                for (const t of manualRemaining) {
                  toggleCompleted(t)
                }
                if (
                  !window.matchMedia('(prefers-reduced-motion: reduce)').matches
                ) {
                  const rect = event.currentTarget.getBoundingClientRect()
                  confetti({
                    particleCount: 90,
                    spread: 70,
                    startVelocity: 26,
                    scalar: 0.85,
                    ticks: 140,
                    origin: {
                      x: (rect.left + rect.width / 2) / window.innerWidth,
                      y: (rect.top + rect.height / 2) / window.innerHeight,
                    },
                    zIndex: 9999,
                    disableForReducedMotion: true,
                  })
                }
              }}
            >
              <CheckCheckIcon />
              Complete all homework
            </Button>
          )}
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => onNavigate(-1)}
            aria-label="Previous week"
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => onNavigate(1)}
            aria-label="Next week"
          >
            <ChevronRightIcon />
          </Button>
          {!isCurrentWeek && (
            <Button variant="outline" size="sm" onClick={onToday}>
              <CalendarDaysIcon />
              This week
            </Button>
          )}
        </div>
      </div>

      <CardBoard
        onPlace={(cardId, slotId) => {
          const task = tasks.find((t) => t.id === cardId)
          if (task && slotId !== task.date) {
            moveTask(cardId, slotId)
            setDroppedTaskId(cardId)
          }
        }}
        onDragStartCard={(cardId) =>
          setDragTask(tasks.find((t) => t.id === cardId) ?? null)
        }
        renderOverlay={(cardId) => {
          const task = tasks.find((t) => t.id === cardId)
          return task ? <TaskCard task={task} /> : null
        }}
      >
        {/* Clears the drag-source opacity on every drag end/cancel, even
            when the drop misses every slot (CardBoard.onPlace only fires
            on a resolved drop) — see task-2-report.md fix round 1. */}
        <DragEndReset onEnd={() => setDragTask(null)} />
        <div className="-mx-4 flex flex-col gap-4 overflow-x-auto px-4 pb-2 md:mx-0 md:px-0">
          {backlogWeeks.length > 0 && (
            <div className="flex min-w-[980px] flex-col gap-3 rounded-xl border border-ready-orange/40 bg-ready-orange/5 p-3">
              <div className="flex items-center gap-2">
                <TriangleAlertIcon
                  className="size-4 text-ready-orange"
                  aria-hidden="true"
                />
                <h3 className="text-sm font-semibold">Missed work</h3>
                <span className="text-sm text-muted-foreground">
                  {missedCount} {missedCount === 1 ? 'task' : 'tasks'} from{' '}
                  {backlogWeeks.length}{' '}
                  {backlogWeeks.length === 1 ? 'past week' : 'past weeks'} —
                  complete them or drag them into this week
                </span>
              </div>
              {backlogWeeks.map((bw) => {
                const bwKey = toKey(bw)
                const bwSprint = sprints.find((s) => s.startDate === bwKey)
                return (
                  <div key={bwKey} className="flex flex-col gap-1.5">
                    <span className="px-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
                      {bwSprint ? `Sprint ${bwSprint.number} · ` : ''}
                      {format(bw, 'MMM d')} – {format(addDays(bw, 6), 'MMM d')}
                    </span>
                    <WeekRow
                      weekStart={bw}
                      locked={false}
                      overdue
                      dragTaskId={dragTask?.id}
                      droppedTaskId={droppedTaskId}
                    />
                  </div>
                )
              })}
            </div>
          )}

          {/* Keyed by week so the entrance stagger replays when navigating */}
          <WeekRow
            key={weekStartKey}
            weekStart={weekStart}
            locked={locked}
            dragTaskId={dragTask?.id}
            droppedTaskId={droppedTaskId}
          />
        </div>
      </CardBoard>
    </section>
  )
}
